import { inject, Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { LoggingService } from './logging.service';
import { ConfigurationService } from '../pages/offline/configuration.service';
import { UserGroup } from '../pages/users/models/user-group.model';
import { Attribute } from '../pages/users/models/attribute.model';
import { User } from '../pages/users/models/user.model';
import { ListUsersResponse } from '../pages/users/models/list-users-response.model';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { AmplifyApiService } from './amplify-api.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  constructor(
    private readonly logger: LoggingService,
    private readonly configurationService: ConfigurationService,
  ) {}

  public fetchUserGroups(): void {
    if (this.onLine) {
      this.getUserGroups()
        .then((userGroups) => {
          this.logger.debug('user-groups', userGroups);
          this.userGroups = userGroups;
          this.configurationService.populateUserGroups(this.userGroups);
        })
        .catch((error) => {
          this.logger.error(error);
        });
    } else {
      this.configurationService
        .getUserGroups()
        .then((userGroups) => {
          this.userGroups = userGroups;
        })
        .catch((error) => {
          this.logger.error(error);
        });
    }
  }

  public async getUserGroups(): Promise<any> {
    return this.API.get(environment.API_NAME, '/v2/user-groups', {});
  }

  public async getUsers(
    filter: unknown,
    groups: boolean,
    limit: any = undefined,
    paginationToken = '',
  ): Promise<ListUsersResponse> {
    if (this.onLine) {
      const users = await this.API.get(
        environment.API_NAME,
        `/v2/users?filters=${encodeURIComponent(
          JSON.stringify(filter),
        )}&groups=${groups}&limit=${limit}&paginationToken=${encodeURIComponent(paginationToken)}`,
        {},
      );
      this.configurationService.populateUsers(users.Users);
      return users;
    } else {
      const users = await this.configurationService.getUsersByFilter(filter);
      return {
        Users: users,
        EstimatedNumberOfUsers: users.length,
        PaginationToken: '',
      };
    }
  }

  public async createUser(user: unknown): Promise<any> {
    return this.API.post(environment.API_NAME, `/v2/users`, { body: user });
  }

  public async updateUser(user: unknown): Promise<any> {
    return this.API.put(environment.API_NAME, `/v2/users`, { body: user });
  }

  public async enableUser(userId: string): Promise<any> {
    return this.API.get(environment.API_NAME, `/v2/users/${userId}/enable`, {});
  }

  public async disableUser(userId: string): Promise<any> {
    return this.API.get(environment.API_NAME, `/v2/users/${userId}/disable`, {});
  }

  public async resetPassword(userId: string): Promise<any> {
    return this.API.get(environment.API_NAME, `/v2/users/${userId}/reset-password`, {});
  }

  public async changePassword(userId: string, password: string, permanent: boolean): Promise<any> {
    return this.API.post(environment.API_NAME, `/v2/users/${userId}/change-password`, {
      body: { password, permanent },
    });
  }

  public getUserAttribute(user: User, attributeName: string): string {
    for (const attribute of user.Attributes) {
      if (attribute.Name === attributeName) {
        return attribute.Value;
      }
    }

    return '';
  }

  public getCompleteUserName(
    attributes: Array<Attribute> | { family_name: string; given_name: string; email: string },
  ): string {
    if (!attributes) {
      return '';
    }

    if (Array.isArray(attributes)) {
      const lastName = attributes?.find((x: Attribute) => x.Name === 'family_name')?.Value;
      const firstName = attributes?.find((x: Attribute) => x.Name === 'given_name')?.Value;
      const email = attributes?.find((x: Attribute) => x.Name === 'email')?.Value;

      return `${lastName}, ${firstName} (${email})`;
    } else {
      const lastName = attributes.family_name ?? '';
      const firstName = attributes.given_name ?? '';
      const email = attributes.email ?? '';

      if (lastName && firstName && email) {
        return `${lastName}, ${firstName} (${email})`;
      }

      if (!lastName && !firstName && email) {
        return email;
      }

      return '';
    }
  }

  public setCurrentUser(email: string): Observable<void> {
    if (!email) {
      throw new Error('email mandatory to setCurrentUser');
    }
    const filter = { field: 'email', value: email, operator: 'eq' };

    return from(this.getUsers(filter, false)).pipe(
      distinctUntilChanged(),
      map((usersResponse) => {
        if (!usersResponse.Users || usersResponse.Users.length === 0) {
          throw new Error('Current user not found');
        }
        return {
          ...usersResponse.Users[0],
          Name: this.getCompleteUserName(usersResponse.Users[0].Attributes),
          Email: this.getUserAttribute(usersResponse.Users[0], 'email'),
        };
      }),
      map((user) => this.currentUserSubject.next(user)),
    );
  }

  private readonly API = inject(AmplifyApiService);
  private readonly currentUserSubject = new BehaviorSubject<User | null>(null);

  public currentUser$ = this.currentUserSubject.asObservable();
  public userGroups: UserGroup[] = [];
  public onLine: boolean = navigator.onLine;
  public decodedToken: any | undefined;
}
